import apiClient from './meta-client';
import type {
    LeadResponse,
    LeadListResponse,
    LeadStatsResponse,
    LeadSyncStartResponse,
    LeadSyncProgressResponse,
} from '@/types/meta-lead';

// ========== LEADS API ==========

// Obter estatísticas gerais
export const getLeadStats = async (params?: {
    page_id?: string;
    tag_id?: number;
    in_funnel?: boolean;
    in_24h_window?: boolean;
    is_active?: boolean;
    search?: string;
    project_ids?: number[];
}): Promise<LeadStatsResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.page_id) queryParams.append('page_id', params.page_id);
    if (params?.tag_id) queryParams.append('tag_id', String(params.tag_id));
    if (typeof params?.in_funnel === 'boolean') queryParams.append('in_funnel', String(params.in_funnel));
    if (typeof params?.in_24h_window === 'boolean') queryParams.append('in_24h_window', String(params.in_24h_window));
    if (typeof params?.is_active === 'boolean') queryParams.append('is_active', String(params.is_active));
    if (params?.search) queryParams.append('search', params.search);
    // FastAPI Optional[List[int]] espera repetir o param: ?project_ids=1&project_ids=2
    if (params?.project_ids?.length) {
        for (const id of params.project_ids) queryParams.append('project_ids', String(id));
    }

    const response = await apiClient.get<LeadStatsResponse>(
        `/leads/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
    return response.data;
};

// Listar leads com paginação e filtros
export const getLeads = async (params?: {
    page?: number;
    per_page?: number;
    page_id?: string;
    tag_id?: number;
    in_funnel?: boolean;
    in_24h_window?: boolean;
    is_active?: boolean;
    search?: string;
    project_ids?: number[];
    sort_by?: 'last_interaction_at' | 'created_at' | 'nome' | 'id';
    sort_order?: 'asc' | 'desc';
}): Promise<LeadListResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.per_page) queryParams.append('per_page', String(params.per_page));
    if (params?.page_id) queryParams.append('page_id', params.page_id);
    if (params?.tag_id) queryParams.append('tag_id', String(params.tag_id));
    if (typeof params?.in_funnel === 'boolean') queryParams.append('in_funnel', String(params.in_funnel));
    if (typeof params?.in_24h_window === 'boolean') queryParams.append('in_24h_window', String(params.in_24h_window));
    if (typeof params?.is_active === 'boolean') queryParams.append('is_active', String(params.is_active));
    if (params?.search) queryParams.append('search', params.search);
    if (params?.project_ids?.length) {
        for (const id of params.project_ids) queryParams.append('project_ids', String(id));
    }
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);

    const response = await apiClient.get<LeadListResponse>(
        `/leads${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
    return response.data;
};

// Obter lead específico
export const getLead = async (leadId: number): Promise<LeadResponse> => {
    const response = await apiClient.get<LeadResponse>(`/leads/${leadId}`);
    return response.data;
};

// ========== SYNC API ==========

// Iniciar sincronização de leads
export const startLeadSync = async (pageIds: number[] = []): Promise<LeadSyncStartResponse> => {
    const response = await apiClient.post<LeadSyncStartResponse>('/leads/sync', {
        page_ids: pageIds,
    });
    return response.data;
};

// Consultar progresso da sincronização
export const getLeadSyncProgress = async (taskId: string): Promise<LeadSyncProgressResponse> => {
    const response = await apiClient.get<LeadSyncProgressResponse>(`/leads/sync/progress/${taskId}`);
    return response.data;
};

// ========== REACTIVATE API ==========

// Reativar leads não visados
export const reactivateLeads = async (leadIds: number[]): Promise<{ reactivated: number; message: string }> => {
    const response = await apiClient.patch<{ reactivated: number; message: string }>('/leads/reactivate', {
        lead_ids: leadIds,
    });
    return response.data;
};
